if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.collection-list__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.layout = this.dataset.layout;
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);

      this.sliderInstance = false;
      this.fixedCardRefCols = 4;

      this.setupFixedWidthListeners();
      this.updateFixedCardWidth();

      if (!this.enableSlider) return;

      this.init();
      document.addEventListener('matchMobile', () => {
        this.init();
      });
      document.addEventListener('unmatchMobile', () => {
        this.init();
      });
    }

    setupFixedWidthListeners() {
      if (this.layout !== 'split') return;

      this.onResize = FoxTheme.utils.debounce(() => {
        this.updateFixedCardWidth();
      }, 100);

      window.addEventListener('resize', this.onResize);

      const content = this.section?.querySelector('.section__content');
      if (content && typeof ResizeObserver !== 'undefined') {
        this.contentResizeObserver = new ResizeObserver(() => {
          this.updateFixedCardWidth();
        });
        this.contentResizeObserver.observe(content);
      }
    }

    updateFixedCardWidth() {
      if (this.layout !== 'split') return;

      const content = this.section?.querySelector('.section__content');
      const gapSource = this.sliderWrapper || this.querySelector('.collection-list__items');
      if (!content || !gapSource) return;

      if (FoxTheme.config.mqlMobile) {
        content.style.removeProperty('--collection-card-width');
        return;
      }

      const columnGap = FoxTheme.utils.getGridColumnGap(gapSource);
      const gap =
        window.innerWidth >= 1280
          ? columnGap.desktop
          : columnGap.tabletLarge || columnGap.desktop;
      const cardWidth =
        (content.clientWidth - (this.fixedCardRefCols - 1) * gap) / this.fixedCardRefCols;

      content.style.setProperty('--collection-card-width', `${Math.max(0, cardWidth)}px`);

      if (this.sliderInstance?.slider) {
        this.sliderInstance.slider.update();
      }
    }

    init() {
      this.updateFixedCardWidth();

      if (FoxTheme.config.mqlMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      if (typeof this.sliderInstance === 'object') {
        this.destroySlider();
      }

      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);
      const useFixedCardWidth = this.layout === 'split';

      const sliderOptions = {
        slidesPerView: useFixedCardWidth ? 'auto' : this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
        spaceBetween: columnGap.tablet,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        breakpoints: useFixedCardWidth
          ? {
              768: {
                slidesPerView: 'auto',
                spaceBetween: columnGap.tablet,
              },
              1024: {
                slidesPerView: 'auto',
                spaceBetween: columnGap.tabletLarge,
              },
              1280: {
                slidesPerView: 'auto',
                spaceBetween: columnGap.desktop,
              },
            }
          : {
              1024: {
                slidesPerView: parseInt(this.items),
                spaceBetween: columnGap.tabletLarge,
              },
              1280: {
                slidesPerView: parseInt(this.items),
                spaceBetween: columnGap.desktop,
              },
            },
        loop: true,
        threshold: 2,
      };

      this.updateFixedCardWidth();

      this.classList.add(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.grid);
      this.sliderWrapper.classList.add(this.classes.swiperWrapper);
      this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
      this.sliderInstance.init();

      const focusableElements = FoxTheme.a11y.getFocusableElements(this);

      focusableElements.forEach((element) => {
        element.addEventListener('focusin', () => {
          const slide = element.closest('.swiper-slide');
          this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
        });
      });

      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          this.sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  }
  customElements.define('collection-list', CollectionList);
}
